import { useMemo } from "react";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { usePlatform } from "@/components/platform/PlatformProvider";
import { useSupportEmail } from "@/hooks/useSupportEmail";
import { usePublicPageMeta } from "@/hooks/usePublicPageMeta";
import { useLiveLegalContent } from "@/hooks/useLiveLegalContent";
import { PageLoader } from "@/components/ui/spinner";
import { renderBlocks, interpolate, type Block, type Section } from "./LegalContent";
import { AutoTranslationDisclosure } from "@/components/layout/AutoTranslationDisclosure";

interface LegalPageLayoutProps {
  pageKey: "terms" | "privacy" | "policies" | "faqProcedures";
  title: string;
  intro?: Block[];
  lastUpdated: string;
  sections: Section[];
  canonical: string;
  seoDescription: string;
  /** Additional content rendered inside the main column above the sections (e.g. accordion). */
  children?: React.ReactNode;
  /**
   * When true, the layout uses `sections` only to populate the TOC sidebar
   * and does not render the section bodies in the main column. The page
   * provides its own body content via `children` (e.g. an accordion).
   */
  tocOnlySections?: boolean;
  /** Optional JSON-LD structured data passed through to applyPageMeta. */
  jsonLd?: Record<string, unknown>;
}

const LEGAL_LINKS: Array<{ href: string; key: LegalPageLayoutProps["pageKey"] }> = [
  { href: "/terms", key: "terms" },
  { href: "/privacy", key: "privacy" },
  { href: "/help/topic/trust-and-safety", key: "policies" },
  { href: "/help/topic/booking-a-car", key: "faqProcedures" },
];

export function LegalPageLayout(props: LegalPageLayoutProps) {
  const { pageKey, canonical, children, tocOnlySections = false } = props;
  const config = usePlatform();
  const supportEmail = useSupportEmail();
  const { t } = useTranslation("legal");
  const legal = useLiveLegalContent(pageKey);
  const live = legal.read === "found" ? legal.value : null;

  const title = live?.title ?? props.title;
  const intro = live?.intro ?? props.intro;
  const lastUpdated = live?.last_updated ?? props.lastUpdated;
  const sections = live?.sections ?? props.sections;
  const seoDescription = live?.seo_description ?? props.seoDescription;
  const jsonLd = live?.jsonLd ?? props.jsonLd;

  const vars = useMemo(
    () => ({
      brand: config.name,
      entity: t("common.entity"),
      supportEmail,
    }),
    [config.name, supportEmail, t],
  );

  const isBac = config.platform === "bac";
  const entityDisclosure = isBac
    ? t("common.entity_disclosure_bac", { brand: config.name })
    : t("common.entity_disclosure_ds", { brand: config.name });

  usePublicPageMeta(useMemo(() => legal.read !== "found" ? null : ({
      title: `${title} · ${config.name}`,
      description: interpolate(seoDescription, vars),
      canonical,
      jsonLd,
    }), [legal.read, title, seoDescription, canonical, config.name, jsonLd, vars]));

  if (legal.read === "error") throw legal.error;
  if (legal.read === "pending") return <PageLoader />;

  return (
    <div className="bg-background pt-16">
      <div className="relative overflow-hidden border-b border-border bg-gradient-to-b from-muted/40 via-muted/20 to-background">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14 lg:px-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
            {t(`${pageKey}.title`)}
          </p>
          <h1 className="mt-2 text-3xl font-bold leading-tight text-foreground sm:text-4xl lg:text-5xl">{title}</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {t("common.last_updated_label")}: <span className="font-medium text-foreground/80">{lastUpdated}</span>
          </p>
          <div className="mt-5 rounded-lg border border-primary/20 material-panel p-4 text-sm text-foreground">
            {entityDisclosure}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[240px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-6">
              <div>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {t("common.on_this_page")}
                </h2>
                <ul className="mt-3 space-y-2 text-sm">
                  {sections.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className="block text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {interpolate(s.title, vars)}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {t("common.related_pages")}
                </h2>
                <ul className="mt-3 space-y-2 text-sm">
                  {LEGAL_LINKS.filter((l) => l.key !== pageKey).map((l) => (
                    <li key={l.href}>
                      <Link href={l.href} className="text-muted-foreground transition-colors hover:text-foreground">
                        {t(`${l.key}.title`)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>

          <article className="max-w-3xl text-foreground">
            <AutoTranslationDisclosure variant="legal" className="mb-6" />
            {intro && intro.length > 0 && (
              <div className="mb-10 text-foreground/90">{renderBlocks(intro, `${pageKey}-intro`, vars)}</div>
            )}
            {children}
            {!tocOnlySections && (
              <div className="space-y-10">
                {sections.map((s) => (
                  <section key={s.id} id={s.id} className="scroll-mt-24">
                    <h2 className="text-xl font-bold text-foreground sm:text-2xl">
                      <span className="mr-3 inline-block h-1.5 w-6 rounded-full bg-primary/70 align-middle" aria-hidden />
                      {interpolate(s.title, vars)}
                    </h2>
                    <div className="mt-3 text-foreground/90">{renderBlocks(s.body, s.id, vars)}</div>
                  </section>
                ))}
              </div>
            )}
            <div className="mt-12 border-t border-border pt-6 text-sm text-muted-foreground">
              <p>
                {t("common.see_also")}{" "}
                {LEGAL_LINKS.filter((l) => l.key !== pageKey).map((l, i, arr) => (
                  <span key={l.href}>
                    <Link href={l.href} className="text-primary underline underline-offset-2 hover:no-underline">
                      {t(`${l.key}.title`)}
                    </Link>
                    {i < arr.length - 1 ? " · " : ""}
                  </span>
                ))}
              </p>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}
